import {HomeView} from "./home";
import * as React from "react";
import {useCallback, useEffect, useState} from "react";
import {
    useDeactivateListener,
    useOccupancies,
    usePlatform
} from "@variocube/cube-ui";
import {borrowerCanAccess, Item, lenderCanAccess, Rental, RentalTypes, User} from "./types";
import {RentalBorrowerView} from "./RentalBorrowerView";
import {AdminView} from "./admin/AdminView";
import {RentalLenderView} from "./RentalLenderView";
import {LogLevel} from "@variocube/app-api";
import {InspectorView} from "./admin/InspectorView";
import {ItemAccessView} from "./ItemAccessView";
import {defined} from "@variocube/app-common";
import {useItems, useItemsForInspection, useRentals, useRentalTypes, useUsers} from "./hooks";
import {logger} from "./logger";
import {BorrowerRentalListView} from "./BorrowerRentalListView";

const log = logger.sub("App");

export function App() {
    const platform = usePlatform();
    const rentals = useRentals();
    const items = useItems();
    const occupancies = useOccupancies();
    const itemsForInspection = useItemsForInspection();
    const users = useUsers();
    const rentalTypesValue = useRentalTypes();
    const rentalTypes = rentalTypesValue || RentalTypes.Loan;

    const [rentalBorrower, setBorrowerRental] = useState<Rental>();
    const [rentalLender, setRentalLender] = useState<Rental>();
    const [borrowerRentals, setBorrowerRentals] = useState<Rental[]>();
    const [itemAdmin, setItemAdmin] = useState<Item|undefined>();
    const [itemAccess, setItemAccess] = useState<Item|undefined>();
    const [user, setUser] = useState<User>();

    useEffect(() => log.info("items", items), [items]);
    useEffect(() => log.info("rentals", rentals), [rentals]);

    useDeactivateListener(async () => {
        log.info(`Triggering useDeactivateListener`);
        setBorrowerRentals(undefined);
        setBorrowerRental(undefined);
        setRentalLender(undefined);
        setItemAdmin(undefined);
        setItemAccess(undefined);
        setUser(undefined);
    });

    const handleRentals = useCallback((rentals: Rental[]) => {
        setBorrowerRentals(rentals);
    }, [])

    const handleCode = useCallback(async (code: string) => {
        await platform.log(LogLevel.Trace, "handleCode in app.tsx", {code});
        if (rentals) {
            await platform.log(LogLevel.Trace, "Checking barcode against rentals", {code});
            const rentalBorrower = rentals.find(rental => (rental.borrower.accessCode == code || rental.borrower.accessKey == code));
            if (rentalBorrower && borrowerCanAccess(rentalBorrower)) {
                setBorrowerRental(rentalBorrower);
                await platform.log(LogLevel.Trace, "Code matched rental.borrower.accessCode or accessKey of rental", {code, rentalUuid: rentalBorrower.uuid});
                return;
            }
            const rentalLender = rentals.find(rental => rental.lender && rental.lender.accessCode == code);
            if (rentalLender && lenderCanAccess(rentalLender)) {
                setRentalLender(rentalLender);
                await platform.log(LogLevel.Trace, "Code matched rental.lender.accessCode of rental", {code, rentalUuid: rentalLender.uuid});
                return;
            }
        }
        if (items) {
            await platform.log(LogLevel.Trace, "Checking barcode against items", {code});
            let i = items.find((i) => i.adminCode == code);
            setItemAdmin(i);
            i = items.find((i) => i.openCode == code);
            setItemAccess(i);
        }
        if (users) {
            await platform.log(LogLevel.Trace, "Checking barcode against users", {code});
            const user = users.find(user => user.accessCode == code);
            if (user) {
                setUser(user);
                await platform.log(LogLevel.Trace, "Code matched user.accessCode of rental", {code, lastName: user.lastName});
                return;
            }
        }
        throw new Error("Invalid code.");
    }, [rentals, items, users]);

    const handleAdminDone = useCallback(() => {
        setUser(undefined);
        setItemAdmin(undefined);
    }, []);

    const handleRentalClose = useCallback(() => {
        setBorrowerRentals(undefined);
        setBorrowerRental(undefined);
        setRentalLender(undefined);
    }, []);

    const handleRentalSelect = useCallback(async (rental: Rental) => {
        if (borrowerCanAccess(rental)) {
            setBorrowerRental(rental);
            setBorrowerRentals(undefined);
            await platform.log(LogLevel.Trace, "Rental selected via rental list from NFC Token", {rentalUuid: rental.uuid});
            return;
        }
    }, [])

    if (borrowerRentals !== undefined && borrowerRentals.length > 0) {
        return (
            <BorrowerRentalListView
                rentals={borrowerRentals}
                items={items}
                occupancies={occupancies}
                onRentalSelect={handleRentalSelect}
                onClose={handleRentalClose}
            />
        )
    }

    if (rentalBorrower) {
        return <RentalBorrowerView rental={rentalBorrower} rentalTypes={rentalTypes} onClose={handleRentalClose} />
    }

    if(rentalLender) {
        return <RentalLenderView rental={rentalLender} rentalTypes={rentalTypes} onClose={handleRentalClose} />
    }

    if (user && user.inspector) {
        const inspectionItems = itemsForInspection.map(uuid => items.find(i => i.uuid === uuid)).filter(defined);
        return (
            <InspectorView
                user={user}
                items={inspectionItems}
            />
        );
    }

    if (itemAdmin && rentals && items) {
        return <AdminView rentals={rentals} items={items} selectedItem={itemAdmin} onDone={handleAdminDone} />
    }

    if (itemAccess) {
        return <ItemAccessView item={itemAccess} rentalTypes={rentalTypes} onClose={() => setItemAccess(undefined)} />
    }

    if (user && !user.inspector && rentals && items) {
        return <AdminView user={user} rentals={rentals} items={items} itemsForInspection={(itemsForInspection || []).map(uuid => items.find(i => i.uuid === uuid)).filter(i => !!i) as Item[]} onDone={handleAdminDone} />
    }

    return (
        <HomeView
            onCode={handleCode}
            onRentals={handleRentals}
            rentalTypes={rentalTypes}
        />
    )
}
